import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { teacherApi } from '../../services/api';
import BookDetailView from '../../components/BookDetailView';

/**
 * Teacher Kütüphanem.
 *
 * The book list lives here; the book-detail view is the shared
 * <BookDetailView mode="teacher"> — identical UI to the student Kitap İçeriği
 * (Üniteler → Alıştırma → İçerik + all the modals), but read-only: no "Teste
 * Başla", and the solution-video viewer shows the answer key inline instead.
 */

const F = { fontFamily: 'Inter' } as const;

interface Book {
  id: number | string;
  title: string;
  display_name?: string | null;
  cover?: string;
  cover_url?: string | null;
  cover_image_url?: string | null;
  subject?: { id: number; name: string } | null;
  grade_levels?: { grade_number: number | null; name: string } | null;
  publishers?: { name: string } | null;
}

const TeacherLibrary: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // B27: teacher may arrive from an assignment's "Soruları Görüntüle" — open the
  // book straight on the target section's solution videos, and send the back
  // button to that assignment instead of the library.
  const incomingAssignmentId = (location.state as any)?.openAssignmentId as number | undefined;
  const incomingBookId = (location.state as any)?.bookId as number | string | undefined;
  const incomingSectionLabel = (location.state as any)?.sectionLabel as string | undefined;

  const [view, setView] = useState<'library' | 'book'>(incomingBookId != null ? 'book' : 'library');
  const [selectedBookId, setSelectedBookId] = useState<number | string | null>(incomingBookId ?? null);
  // Deep-link section, consumed on the first book open.
  const autoSectionRef = useRef<string | undefined>(incomingSectionLabel);
  const [autoSection, setAutoSection] = useState<string | undefined>(incomingSectionLabel);

  const [books, setBooks] = useState<Book[]>([]);
  const [subjectFilter, setSubjectFilter] = useState<number | null>(null);
  const [loadingBooks, setLoadingBooks] = useState(true);

  // Kitap Ekle modal state — same UX as student Library
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemSubmitting, setRedeemSubmitting] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [redeemSuccess, setRedeemSuccess] = useState<string | null>(null);

  const reloadLibrary = useCallback(() => {
    setLoadingBooks(true);
    teacherApi.library()
      .then((res) => setBooks(res.data || []))
      .catch(console.error)
      .finally(() => setLoadingBooks(false));
  }, []);

  useEffect(() => { reloadLibrary(); }, [reloadLibrary]);

  const submitRedeem = async () => {
    setRedeemError(null); setRedeemSuccess(null);
    const code = redeemCode.trim().toUpperCase();
    if (!code) { setRedeemError('Lütfen kodunuzu girin.'); return; }
    setRedeemSubmitting(true);
    try {
      const r = await teacherApi.redeemBookCode(code);
      setRedeemSuccess(`"${r.data.book.title}" kütüphanenize eklendi.`);
      setRedeemCode('');
      reloadLibrary();
      setTimeout(() => { setRedeemOpen(false); setRedeemSuccess(null); }, 1800);
    } catch (e: any) {
      setRedeemError(e?.response?.data?.error ?? e.message);
    } finally {
      setRedeemSubmitting(false);
    }
  };

  const openBook = (id: number | string) => {
    setSelectedBookId(id);
    // Deep-link section only applies to the book we arrived on; clear it once
    // the teacher opens any other book from the list.
    setAutoSection(id === incomingBookId ? autoSectionRef.current : undefined);
    setView('book');
  };

  const exitBook = () => {
    if (incomingAssignmentId != null) { navigate(`/teacher/odevlerim/${incomingAssignmentId}`); return; }
    setView('library');
    setSelectedBookId(null);
    setAutoSection(undefined);
  };

  // ---- BOOK CONTENT VIEW (shared, read-only) ----
  if (view === 'book' && selectedBookId != null) {
    return (
      <BookDetailView
        mode="teacher"
        bookId={selectedBookId}
        autoOpenSection={autoSection}
        autoOpenVideos={!!autoSection}
        onExit={exitBook}
      />
    );
  }

  // ---- LIBRARY VIEW ----
  // Multi-branch teachers (e.g. someone teaching both Matematik and Fizik) see
  // books from every assigned subject. Surface a subject filter so they can
  // narrow the list. Single-branch teachers don't see the dropdown.
  const subjectsInLibrary = Array.from(
    new Map(
      books.filter(b => b.subject?.id).map(b => [b.subject!.id, b.subject!]),
    ).values(),
  ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  const filteredBooks = subjectFilter == null
    ? books
    : books.filter(b => b.subject?.id === subjectFilter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4">
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Kütüphanem</h1>
        <div className="flex items-center gap-3">
          {subjectsInLibrary.length > 1 && (
            <select
              value={subjectFilter ?? ''}
              onChange={(e) => setSubjectFilter(e.target.value === '' ? null : Number(e.target.value))}
              className="rounded-[12px] px-4 py-2 border border-gray-200 bg-white text-[14px]"
              style={{ ...F, fontWeight: 500, color: '#2b2b2b' }}
            >
              <option value="">Tüm Branşlar</option>
              {subjectsInLibrary.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => { setRedeemOpen(true); setRedeemError(null); setRedeemSuccess(null); }}
            className="bg-[#e5599b] text-white text-[14px] lg:text-[16px] font-semibold px-5 py-3 rounded-[12px] flex items-center gap-2 hover:bg-pink-600 transition-colors"
          >
            <span className="text-[16px] lg:text-[20px]">+</span> Kitap Ekle
          </button>
        </div>
      </div>

      {redeemOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => !redeemSubmitting && setRedeemOpen(false)} />
          <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[520px] p-8" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => !redeemSubmitting && setRedeemOpen(false)}
              className="absolute top-5 right-5 w-[44px] h-[44px] rounded-full flex items-center justify-center hover:bg-gray-200"
              aria-label="Kapat"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
                <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
              </svg>
            </button>
            <h2 style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b', marginBottom: 8 }}>Kitap Ekle</h2>
            <p style={{ ...F, fontSize: 13, color: '#666', marginBottom: 18 }}>
              Kitabınızın içindeki aktivasyon kodunu girin. Her kod tek seferlik kullanılır.
            </p>
            <label className="block mb-4">
              <span style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>
                Aktivasyon Kodu
              </span>
              <input
                type="text"
                placeholder="PLE-T-XXXXXX-XXXX"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter' && !redeemSubmitting) submitRedeem(); }}
                disabled={redeemSubmitting}
                autoFocus
                className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[16px] font-mono"
                style={{ border: '2px solid #e5599b', letterSpacing: 1, ...F }}
              />
            </label>
            {redeemError && (
              <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>
                {redeemError}
              </div>
            )}
            {redeemSuccess && (
              <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#1f7a35]" style={{ background: '#dff5e0' }}>
                {redeemSuccess}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setRedeemOpen(false)}
                disabled={redeemSubmitting}
                className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50"
                style={{ background: '#e5e5e5' }}
              >
                <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>İptal</span>
              </button>
              <button
                onClick={submitRedeem}
                disabled={redeemSubmitting || !redeemCode.trim()}
                className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50"
                style={{ background: '#e5599b' }}
              >
                <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>
                  {redeemSubmitting ? 'Aktive ediliyor…' : 'Kitabı Ekle'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {loadingBooks ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : filteredBooks.length === 0 ? (
        <p className="text-center text-gray-400 py-16">
          {subjectFilter != null ? 'Seçili branşta kitap bulunamadı.' : 'Kütüphanenizde kitap bulunamadı.'}
        </p>
      ) : (
        <div className="flex gap-5 flex-wrap">
          {filteredBooks.map((book, i) => (
            <div
              key={book.id}
              onClick={() => openBook(book.id)}
              className="bg-[#fafafa] rounded-[24px] p-5 flex gap-5 w-full max-w-[600px] cursor-pointer hover:shadow-md transition-shadow border border-gray-100"
            >
              <div className="w-[130px] h-[160px] rounded-[12px] overflow-hidden flex-shrink-0 bg-gray-100">
                <img
                  src={book.cover_url || book.cover_image_url || book.cover || `/a-book${(i % 5) + 1}.png`}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 flex flex-col justify-center">
                <p style={{ ...F, fontSize: 20, fontWeight: 500, color: '#2b2b2b', marginBottom: 12 }}>
                  {book.display_name ?? book.title}
                </p>
                {book.subject?.name && (
                  <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#3973bf', marginBottom: 4 }}>{book.subject.name}</p>
                )}
                {book.grade_levels?.grade_number && (
                  <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#888' }}>{book.grade_levels.grade_number}. Sınıf</p>
                )}
                {book.publishers?.name && (
                  <p style={{ ...F, fontSize: 13, color: '#888', marginTop: 8 }}>Yayınevi: {book.publishers.name}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherLibrary;
